<template>
  <div>
    <button
      type="button"
      class="btn btn-primary btn-with-addon"
      @click="showModal"
    >
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-send" />
      </span>
      Notification
    </button>
    <a-modal
      v-model="visible"
      title="Send a new notification"
      :width="1000"
      :destroyOnClose="true"
      :maskClosable="false"
      class="notification-modal"
    >
      <template slot="footer">
        <div class="d-flex justify-content-end px-3 pb-2">
          <a-button key="back" @click="handleCancel" size="large" class="mr-2 rounded-pill">Cancel</a-button>
          <a-button key="submit" type="primary" :loading="loading" @click="handleSubmit" size="large" class="rounded-pill px-4">
            <a-icon type="send" /> Send Notification
          </a-button>
        </div>
      </template>

      <a-form :form="form" layout="vertical" class="px-3">
        <div class="row">
          <!-- Left Column: Message Details -->
          <div class="col-md-7 pr-md-4 border-right">
            <div class="mb-4">
              <p class="section-title">Delivery Method</p>
              <div class="d-flex align-items-center justify-content-between">
                <a-checkbox-group
                  v-model="selectedTypes"
                  class="delivery-checkboxes"
                >
                  <a-checkbox value="push">App Push</a-checkbox>
                  <a-checkbox value="email">Email</a-checkbox>
                  <a-checkbox value="sms">SMS</a-checkbox>
                </a-checkbox-group>

                <div v-if="selectedTypes.includes('sms')" class="sms-balance-badge">
                  SMS Balance: <span class="balance-count">{{ smsBalance }}</span>
                </div>
              </div>

              <div v-if="selectedTypes.includes('sms')" class="mt-3">
                <a-select v-model="selectedTemplate" placeholder="Choose SMS Template" style="width: 100%">
                  <a-select-option :value="0" disabled>Choose SMS Template</a-select-option>
                  <a-select-option v-for="item in templates" :key="item.id" :value="item.id">
                    {{ item.template_name }}
                  </a-select-option>
                </a-select>
              </div>
            </div>

            <a-form-item label="Subject" v-if="selectedTypes.includes('push') || selectedTypes.includes('email')">
              <a-input
                placeholder="e.g., Important Parent-Teacher Meeting Update"
                v-decorator="['subject', { rules: [{ required: true, message: 'Please input a subject!' }] }]"
                size="large"
              />
            </a-form-item>

            <a-form-item label="Message">
              <a-textarea
                placeholder="Type your message here (Optional if a file is attached)..."
                :autoSize="{ minRows: 6, maxRows: 10 }"
                :disabled="isTextareaDisabled"
                v-decorator="['message']"
              />
              <small class="text-muted mt-1 d-block">
                <i class="fe fe-info mr-1"></i> Message is optional if a file attachment is uploaded.
              </small>
            </a-form-item>

            <div class="mt-3">
              <a-upload
                v-decorator="['file']"
                :fileList="fileList"
                :multiple="false"
                :remove="handleRemove"
                :beforeUpload="beforeUpload"
                class="compact-upload"
              >
                <a-button size="large" class="attach-btn">
                  <a-icon type="paper-clip" /> Attach File
                </a-button>
              </a-upload>
            </div>
          </div>

          <!-- Right Column: Target Audience -->
          <div class="col-md-5 pl-md-4">
            <div class="target-panel p-3">
              <p class="section-title">Target Audience</p>

              <!-- Students Target -->
              <div class="audience-card mb-3">
                <div class="d-flex align-items-center mb-2">
                  <a-checkbox v-model="stCheck" class="mr-2"></a-checkbox>
                  <i class="fe fe-users mr-2 text-primary"></i>
                  <span class="font-weight-bold">Students Target</span>
                </div>

                <div v-if="stCheck" class="mt-2 transition-area">
                  <a-radio-group v-model="stSelectionType" size="small" button-style="solid" class="w-100 mb-3 segmented-control">
                    <a-radio-button value="all" class="flex-grow-1 text-center">All</a-radio-button>
                    <a-radio-button value="class" class="flex-grow-1 text-center">By Class</a-radio-button>
                    <a-radio-button value="individual" class="flex-grow-1 text-center">Individual</a-radio-button>
                  </a-radio-group>

                  <!-- By Class Selection -->
                  <div v-if="stSelectionType === 'class'" class="class-selection-area p-2 bg-white rounded border">
                    <div class="row">
                      <div class="col-6 mb-2">
                        <small class="text-muted font-weight-bold">Session</small>
                        <a-select v-model="selectedSession" size="small" style="width: 100%" @change="handleSessionChange">
                          <a-select-option v-for="s in schoolSessions" :key="s.id" :value="s.id">{{ s.name }}</a-select-option>
                        </a-select>
                      </div>
                      <div class="col-6 mb-2">
                        <small class="text-muted font-weight-bold">Class</small>
                        <a-select mode="multiple" v-model="selectedClass" size="small" style="width: 100%" @change="handleClassChange" placeholder="Select Class">
                          <a-select-option :value="0">All Classes</a-select-option>
                          <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">{{ c.name }}</a-select-option>
                        </a-select>
                      </div>
                      <div class="col-12">
                        <small class="text-muted font-weight-bold">Section</small>
                        <div class="mt-1 d-flex flex-wrap" v-if="selectedClass.length === 1 && !selectedClass.includes(0)">
                           <a-checkbox v-for="sec in sections" :key="sec.id" :value="sec.id" class="mr-3 mb-1" @change="(e) => handleSectionToggle(e, sec.id)">
                             {{ sec.name }}
                           </a-checkbox>
                           <span v-if="sections.length === 0" class="text-muted small italic">No sections found</span>
                        </div>
                        <div v-else class="text-muted small">Available for single class selection</div>
                      </div>
                    </div>
                  </div>

                  <!-- Individual Selection -->
                  <div v-if="stSelectionType === 'individual'" class="mt-2">
                    <a-select
                      mode="multiple"
                      v-model="individualStudents"
                      placeholder="Search students..."
                      style="width: 100%"
                      show-search
                      :filter-option="false"
                      :not-found-content="fetchingStudents ? undefined : null"
                      @search="handleStudentSearch"
                    >
                      <a-spin v-if="fetchingStudents" slot="notFoundContent" size="small" />
                      <a-select-option v-for="st in studentList" :key="st.id" :value="st.id">
                        {{ st.first_name }} {{ st.last_name }} ({{ st.registration_no }})
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>

              <!-- Teachers Target -->
              <div class="audience-card">
                <div class="d-flex align-items-center mb-2">
                  <a-checkbox v-model="trCheck" class="mr-2"></a-checkbox>
                  <i class="fe fe-briefcase mr-2 text-primary"></i>
                  <span class="font-weight-bold">Teachers Target</span>
                </div>

                <div v-if="trCheck" class="mt-2 transition-area">
                  <a-radio-group v-model="trSelectionType" size="small" button-style="solid" class="w-100 mb-3 segmented-control">
                    <a-radio-button value="all" class="flex-grow-1 text-center">All</a-radio-button>
                    <a-radio-button value="individual" class="flex-grow-1 text-center">Individual</a-radio-button>
                  </a-radio-group>

                  <!-- Individual Teacher Selection -->
                  <div v-if="trSelectionType === 'individual'" class="mt-2">
                    <a-select
                      mode="multiple"
                      v-model="individualTeachers"
                      placeholder="Search teachers..."
                      style="width: 100%"
                      show-search
                      :filter-option="false"
                      :not-found-content="fetchingTeachers ? undefined : null"
                      @search="handleTeacherSearch"
                    >
                      <a-spin v-if="fetchingTeachers" slot="notFoundContent" size="small" />
                      <a-select-option v-for="tr in teacherList" :key="tr.id" :value="tr.id">
                        {{ tr.first_name }} {{ tr.last_name }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { find, debounce } from 'lodash'

export default {
  name: 'SendNotification',
  data: function() {
    return {
      visible: false,
      loading: false,
      form: this.$form.createForm(this),

      // Delivery
      selectedTypes: ['push'],
      smsBalance: 0,
      templates: [],
      selectedTemplate: 0,
      fileList: [],

      // Audience
      stCheck: true,
      trCheck: false,
      stSelectionType: 'all', // all, class, individual
      trSelectionType: 'all', // all, individual

      // Class selection
      schoolSessions: [],
      schoolClasses: [],
      sections: [],
      selectedSession: undefined,
      selectedClass: [],
      selectedSections: [],
      sectionSpinning: false,

      // Individual selection
      fetchingStudents: false,
      fetchingTeachers: false,
      individualStudents: [],
      individualTeachers: [],
      studentList: [],
      teacherList: [],
    }
  },
  created() {
    this.handleStudentSearch = debounce(this.handleStudentSearch, 500)
    this.handleTeacherSearch = debounce(this.handleTeacherSearch, 500)
  },
  mounted() {
    this.fetchInitialData()
  },
  computed: {
    ...mapState(['settings', 'user']),
    currentSession() {
      return find(this.schoolSessions, ['is_current', 1])
    },
    isTextareaDisabled() {
      if (this.selectedTemplate !== 0) {
        const selected = this.templates.find(item => item.id === this.selectedTemplate)
        if (selected) {
          this.$nextTick(() => {
            this.form.setFieldsValue({ message: selected.template })
          })
        }
      }
      return false
    },
  },
  methods: {
    fetchInitialData() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        const current = find(data.data, ['is_current', 1])
        if (current) this.selectedSession = current.id
        this.getClasses()
      })
      this.getTemplates()
    },
    showModal() {
      this.resetForm()
      this.visible = true
      this.getSmsBalance()
    },
    getTemplates() {
      this.$http.get('/api/sms/templates').then(({ data }) => {
        this.templates = data.data
      })
    },
    getSmsBalance() {
      this.$http.get('/api/sms/balance').then(({ data }) => {
        this.smsBalance = data.data.balance || 0
      })
    },
    getClasses() {
      this.$http.get('/api/class', { params: { session_id: this.selectedSession } }).then(({ data }) => {
        this.schoolClasses = data.data
      })
    },
    getClassSections(classId) {
      this.sectionSpinning = true
      this.$http.get(`/api/section/class/${classId}`).then(({ data }) => {
        this.sections = data.data
        this.sectionSpinning = false
      })
    },
    handleSessionChange(val) {
      this.selectedClass = []
      this.selectedSections = []
      this.getClasses()
    },
    handleClassChange(val) {
      if (val.includes(0)) {
        this.selectedClass = [0]
        this.sections = []
      } else {
        this.selectedClass = val
        if (val.length === 1) {
          this.getClassSections(val[0])
        } else {
          this.sections = []
        }
      }
      this.selectedSections = []
    },
    handleSectionToggle(e, id) {
      if (e.target.checked) {
        this.selectedSections.push(id)
      } else {
        this.selectedSections = this.selectedSections.filter(sid => sid !== id)
      }
    },
    handleStudentSearch(value) {
      if (!value) return
      this.fetchingStudents = true
      this.$http.get('/api/student', { params: { search: value, limit: 10 } }).then(({ data }) => {
        this.studentList = data.data
        this.fetchingStudents = false
      })
    },
    handleTeacherSearch(value) {
      if (!value) return
      this.fetchingTeachers = true
      this.$http.get('/api/teacher', { params: { search: value, limit: 10 } }).then(({ data }) => {
        this.teacherList = data.data
        this.fetchingTeachers = false
      })
    },
    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          if (!values.message && this.fileList.length === 0) {
            this.$message.error('Please enter a message or attach a file.')
            return
          }
          if (this.selectedTypes.length === 0) {
            this.$message.error('Please select at least one delivery method')
            return
          }
          if (!this.stCheck && !this.trCheck) {
            this.$message.error('Please select at least one target audience')
            return
          }

          this.$confirm({
            title: 'Send Notification?',
            content: 'Are you sure you want to send this notification to the selected audience?',
            onOk: () => {
              this.loading = true

              const payload = {
                subject: values.subject,
                message: values.message,
                sms: this.selectedTypes.includes('sms') ? 1 : 0,
                email: this.selectedTypes.includes('email') ? 1 : 0,
                push: this.selectedTypes.includes('push') ? 1 : 0,
                sms_template_id: this.selectedTemplate,
                files: this.fileList,

                student: this.stCheck ? 1 : 0,
                father: 0,
                mother: 0,
                guardian: 0,
                teacher: this.trCheck ? 1 : 0,

                // Targets
                class_id: this.stCheck && this.stSelectionType === 'class' ? (this.selectedClass.includes(0) ? [0] : this.selectedClass) : (this.stCheck && this.stSelectionType === 'individual' ? null : [0]),
                section_id: this.stCheck && this.stSelectionType === 'class' && this.selectedSections.length ? this.selectedSections : (this.stCheck && this.stSelectionType === 'individual' ? null : 0),
                student_id: this.stCheck && this.stSelectionType === 'individual' ? this.individualStudents : [],
                teacher_id: this.trCheck && this.trSelectionType === 'individual' ? this.individualTeachers : [],
              }

              const formData = window.jsonToFormData(payload)
              this.$http.post('/api/notifications/send', formData).then(() => {
                this.$emit('created')
                this.$notification.success({ message: 'Success', description: 'Notification sent successfully' })
                this.visible = false
                this.resetForm()
              }).finally(() => { this.loading = false })
            },
          })
        }
      })
    },
    resetForm() {
      if (this.form) this.form.resetFields()
      this.selectedTypes = ['push']
      this.stCheck = true
      this.trCheck = false
      this.stSelectionType = 'all'
      this.trSelectionType = 'all'
      this.fileList = []
      this.individualStudents = []
      this.individualTeachers = []
      this.selectedClass = []
      this.selectedSections = []
      this.studentList = []
      this.teacherList = []
    },
    handleCancel() {
      this.visible = false
    },
    handleRemove(file) {
      this.fileList = this.fileList.filter(f => f.uid !== file.uid)
    },
    beforeUpload(file) {
      if (file.size / 1024 / 1024 > 300) {
        this.$message.error('File must be smaller than 300MB!')
        return false
      }
      this.fileList = [file]
      return false
    },
  },
}
</script>

<style scoped>
.notification-modal >>> .ant-modal-header {
  border-bottom: none;
  padding: 24px 24px 0;
}
.notification-modal >>> .ant-modal-body {
  padding-top: 12px;
}
.notification-modal >>> .ant-modal-footer {
  border-top: none;
  padding: 0 12px 24px;
}

.section-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.delivery-checkboxes >>> .ant-checkbox-wrapper {
  font-weight: 500;
  margin-right: 24px;
}

.sms-balance-badge {
  background: #fef2f2;
  color: #991b1b;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.balance-count {
  color: #ef4444;
}

.target-panel {
  background: #f8fafc;
  border-radius: 12px;
  height: 100%;
}

.audience-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  transition: all 0.3s ease;
}

.audience-card:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.segmented-control {
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 8px;
  border: none;
}

.segmented-control >>> .ant-radio-button-wrapper {
  background: transparent;
  border: none;
  border-radius: 6px !important;
  box-shadow: none !important;
  color: #64748b;
  font-weight: 500;
  height: 28px;
  line-height: 28px;
}

.segmented-control >>> .ant-radio-button-wrapper-checked {
  background: white !important;
  color: #0f172a !important;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1) !important;
}

.segmented-control >>> .ant-radio-button-wrapper:before {
  display: none;
}

.attach-btn {
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.attach-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.class-selection-area {
  border-color: #f1f5f9 !important;
}

.transition-area {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
